import React, { useImperativeHandle, useMemo, useRef, useState } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { apiCreateUser } from '@/api/user/createUser.ts'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { useTranslation } from 'react-i18next'
import DeviceSerialCombobox from '@/component/input/DeviceSerialCombobox.tsx'

export interface IProps {
    className?: string
    centerId?: number
    onCreated?: () => void
}

/**
 * 사용자 입력(YYYYMMDD, YYYY-MM-DD 등)을 ISO 8601 형식으로 변환
 */
const toISO8601 = (input: string): string => {
    const cleaned = input.replace(/[^0-9]/g, '')
    if (cleaned.length !== 8) return input
    const y = cleaned.substring(0, 4)
    const m = cleaned.substring(4, 6)
    const d = cleaned.substring(6, 8)
    return new Date(`${y}-${m}-${d}T00:00:00.000Z`).toISOString()
}

const AddUserModal = ({ centerId, onCreated }: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const genderOptions = useMemo<ISelectOption[]>(() => [
        { id: '0', text: t('common.gender.male'), value: 0 },
        { id: '1', text: t('common.gender.female'), value: 1 }
    ], [t])

    const [name, setName] = useState('')
    const [gender, setGender] = useState<number>(0)
    const [birthDate, setBirthDate] = useState('')
    const [contact, setContact] = useState('')
    const [address, setAddress] = useState('')
    const [deviceSerial, setDeviceSerial] = useState('')
    const [primaryRelation, setPrimaryRelation] = useState('')
    const [primaryContact, setPrimaryContact] = useState('')
    const [inspectorMemo, setInspectorMemo] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const resetForm = () => {
        setName('')
        setGender(0)
        setBirthDate('')
        setContact('')
        setAddress('')
        setDeviceSerial('')
        setPrimaryRelation('')
        setPrimaryContact('')
        setInspectorMemo('')
    }

    useImperativeHandle(ref, () => ({
        show: () => {
            resetForm()
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    const onClickSubmit = async () => {
        if (!name.trim()) {
            toast.error(t('account.protectee.add.error.nameRequired'))
            return
        }
        if (!birthDate.trim()) {
            toast.error(t('account.protectee.add.error.birthDateRequired'))
            return
        }
        if (!contact.trim()) {
            toast.error(t('account.protectee.add.error.contactRequired'))
            return
        }
        if (!address.trim()) {
            toast.error(t('account.protectee.add.error.addressRequired'))
            return
        }

        if (!centerId) {
            toast.error(t('account.protectee.add.error.centerUnavailable'))
            return
        }

        setIsLoading(true)
        try {
            const requestBody = {
                centerId,
                name: name.trim(),
                gender,
                birthDate: toISO8601(birthDate.trim()),
                contact: contact.trim(),
                address: address.trim(),
                addressLatitude: 0,
                addressLongitude: 0,
                deviceSerial: deviceSerial.trim() || undefined,
                primaryRelation: primaryRelation.trim() || undefined,
                primaryContact: primaryContact.trim() || undefined,
                inspectorMemo: inspectorMemo.trim() || undefined
            }
            console.log('[AddUserModal] createUser request:', JSON.stringify(requestBody))
            const { code, data } = await apiCreateUser(requestBody)
            console.log('[AddUserModal] createUser response:', { code, data })

            if (Number(code) >= 200 && Number(code) < 300) {
                toast.success(t('account.protectee.add.success'))
                await queryClient.invalidateQueries({
                    queryKey: QueryCacheKeys.user.getUsersRoot()
                })
                onCreated?.()
                modalRef.current?.close()
            } else {
                toast.error(t('account.protectee.add.error.fail'))
            }
        } catch {
            toast.error(t('account.protectee.add.error.fail'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <BaseModal
            ref={modalRef}
            showCloseButton={true}
            title={t('account.protectee.add.title')}
            width={'600'}
        >
            <div className={'px-[30px] pb-[30px] max-h-[70vh] overflow-y-auto'}>
                <div className={'grid gap-[16px]'}>
                    {/* 이름 (필수) */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.add.name')}{' '}
                            <span className={'text-red-500'}>*</span>
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={name}
                            placeholder={t('account.protectee.add.name.placeholder')}
                            onChanged={(text) => setName(text)}
                        />
                    </div>

                    {/* 성별 / 생년월일 (필수) */}
                    <div className={'grid grid-cols-2 gap-[12px]'}>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('users.detail.gender')} <span className={'text-red-500'}>*</span>
                            </label>
                            <Select
                                className={'w-full'}
                                options={genderOptions}
                                selectedId={String(gender)}
                                onChanged={(option) => setGender(option.value)}
                            />
                        </div>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('users.detail.birthDate')}{' '}
                                <span className={'text-red-500'}>*</span>
                            </label>
                            <Input
                                inputClassName={'w-full'}
                                value={birthDate}
                                placeholder={'YYYY-MM-DD'}
                                onChanged={(text) => setBirthDate(text)}
                            />
                        </div>
                    </div>

                    {/* 연락처 (필수) */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.add.contact')}{' '}
                            <span className={'text-red-500'}>*</span>
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={contact}
                            placeholder={t('account.protectee.add.contact.placeholder')}
                            onChanged={(text) => setContact(text)}
                        />
                    </div>

                    {/* 주소 (필수) */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('users.detail.address')} <span className={'text-red-500'}>*</span>
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={address}
                            placeholder={t('account.protectee.add.address.placeholder')}
                            onChanged={(text) => setAddress(text)}
                        />
                    </div>

                    {/* 디바이스 시리얼 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.table.deviceSerial')}
                        </label>
                        <DeviceSerialCombobox
                            value={deviceSerial}
                            onChange={setDeviceSerial}
                        />
                    </div>

                    {/* 보호자 정보 */}
                    <div className={'grid grid-cols-2 gap-[12px]'}>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('account.protectee.add.primaryRelation')}
                            </label>
                            <Input
                                inputClassName={'w-full'}
                                value={primaryRelation}
                                placeholder={t('account.protectee.add.primaryRelation.placeholder')}
                                onChanged={(text) => setPrimaryRelation(text)}
                            />
                        </div>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('account.protectee.add.primaryContact')}
                            </label>
                            <Input
                                inputClassName={'w-full'}
                                value={primaryContact}
                                placeholder={t('account.protectee.add.primaryContact.placeholder')}
                                onChanged={(text) => setPrimaryContact(text)}
                            />
                        </div>
                    </div>

                    {/* 주의사항 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('users.detail.memo.title')}
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={inspectorMemo}
                            placeholder={t('account.protectee.add.memo.placeholder')}
                            onChanged={(text) => setInspectorMemo(text)}
                        />
                    </div>
                </div>

                <p className={'text-[12px] text-gray-400 mt-[12px]'}>
                    <span className={'text-red-500'}>*</span>{' '}
                    {t('account.protectee.add.requiredFields')}
                </p>

                <div className={'flex gap-[10px] mt-[20px]'}>
                    <BorderButton
                        text={t('account.protectee.edit.cancel')}
                        className={'flex-1'}
                        onClick={() => modalRef?.current?.close()}
                    />
                    <SolidButton
                        text={t('account.protectee.add.submit')}
                        className={'flex-1'}
                        isLoading={isLoading}
                        onClick={onClickSubmit}
                    />
                </div>
            </div>
        </BaseModal>
    )
}

export default React.forwardRef(AddUserModal)
